/**
 * Under /apps/:appSlug/admin — renders the app's admin page.
 * llm-chat → LLMChatAdmin, localtea → LocalTeaAdmin, bitzbuggy → BitzBuggyAdmin,
 * surftime → SurfTimeAdmin,
 * anything else → the generic platform Admin page.
 *
 * Gating lives on the route in App.tsx (`RequirePlatformAdmin`); this
 * component performs no permission lookup (spec 020 §10 — fixes #84).
 * Lazy per T062/FR-032 so no app chunk joins the entry bundle.
 */
import { lazy, Suspense } from 'react';
import { useParams } from 'react-router-dom';

import { RouteFallback } from '@/components/common';
import { Admin } from '@/pages';
import { withChunkReload } from '@/lib/chunkReload';

const LLMChatAdmin = lazy(
  withChunkReload(() => import('@/apps/llm-chat').then((m) => ({ default: m.LLMChatAdmin }))),
);
const LocalTeaAdmin = lazy(
  withChunkReload(() => import('@/apps/localtea').then((m) => ({ default: m.LocalTeaAdmin }))),
);
const BitzBuggyAdmin = lazy(
  withChunkReload(() => import('@/apps/bitzbuggy').then((m) => ({ default: m.BitzBuggyAdmin }))),
);
// 021: without this branch `/apps/surftime/admin` matched here first and fell
// through to the generic platform Admin, so SurfTime's own admin page was
// unreachable by direct URL (021 fix-wave gate defect #1).
const SurfTimeAdmin = lazy(
  withChunkReload(() => import('@/apps/surftime').then((m) => ({ default: m.SurfTimeAdmin }))),
);

export default function AppAdminBySlug() {
  const { appSlug } = useParams<{ appSlug: string }>();
  if (appSlug === 'llm-chat') {
    return (
      <Suspense fallback={<RouteFallback />}>
        <LLMChatAdmin />
      </Suspense>
    );
  }
  if (appSlug === 'localtea') {
    return (
      <Suspense fallback={<RouteFallback />}>
        <div className="theme-localtea min-h-screen">
          <LocalTeaAdmin />
        </div>
      </Suspense>
    );
  }
  if (appSlug === 'bitzbuggy') {
    return (
      <Suspense fallback={<RouteFallback />}>
        <BitzBuggyAdmin />
      </Suspense>
    );
  }
  if (appSlug === 'surftime') {
    return (
      <Suspense fallback={<RouteFallback />}>
        <SurfTimeAdmin />
      </Suspense>
    );
  }
  return <Admin />;
}
